import { useState, useCallback, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Modal } from 'react-responsive-modal';
import 'react-responsive-modal/styles.css';
import { ReactComponent as CloseIcon } from "../images/icons/white-close.svg";
import { useGenerateDispatch, useGenerateStatus } from "../context/GenerateContext";
import { usePromptusAPIState } from "../context/PromptusAPIContext";
import { useAccountContext } from "../context/AccountContext";
import { useInstallContext } from "../context/InstallContext";
import { generatePrompt, getApiBaseUrl, getProgress, getGenerateOutput, getAuthorizationToken, getModels } from "../api-helper";
import axios from 'axios';
import Swal from 'sweetalert2';
import electronBridge from '../utils/electronBridge';
import { db } from '../db';
import { getAudioMimeFromBase64 } from '../utils/audioUtils';
import { saveGeneratedMedia } from '../services/MediaStorageService';
import { getUnifiedOfflineModels, getOnlineAvailableModels, getLocalAvailableModels } from '../services/offlineModelService';
import { useCosyflowHandlers } from '../hooks/useCosyflowHandlers';
import { isStaff, isStaffOrCreator } from '../utils/accountTypes';
import { hasSupportedTypes, getUnsupportedTypes } from '../components/DynamicPromptForm';

// Import components
import { CosyflowPicker } from '../components/CosyflowPicker/CosyflowPicker';
import { EditCosyflowModal } from '../components/CosyflowPicker/EditCosyflowModal';
import { CreateTemplateWizard } from '../components/CreateTemplateWizard/CreateTemplateWizard';
import { CreatorProfileModal } from '../components/CosyflowPicker/CreatorProfileModal';
// Base cost added to all generations (matches main PWA and templates app)
const BASE_COST = 6;

// Cache configuration for instant loading
const CACHE_KEY = 'cosyflows_api_cache_v3';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

const COSYFLOW_PICKER_CLASS_NAMES = {
    root: 'bg-dark-blue',
    header: 'bg-dark-blue border-b border-white/10',
};

export default function Templates() {
    const navigate = useNavigate();
    const location = useLocation();
    const generateStatusDispatch = useGenerateDispatch();
    const generateStatus = useGenerateStatus();
    const apiState = usePromptusAPIState();
    const accountContext = useAccountContext();
    const { startInstall, isInstalling } = useInstallContext();

    // Check if we should show only user's cosyflows (from navigation state)
    const initialShowOnlyMine = location.state?.showOnlyMine || false;

    // Wizard state
    const [isWizardOpen, setIsWizardOpen] = useState(false);
    const [selectedCosyflow, setSelectedCosyflow] = useState(null);

    // Open wizard if navigated from TemplateDetail with openWizard state
    useEffect(() => {
        if (location.state?.openWizard && location.state?.wizardCosyflow) {
            setSelectedCosyflow(location.state.wizardCosyflow);
            setIsWizardOpen(true);
            // Clear the state so it doesn't re-trigger on re-renders
            navigate(location.pathname, { replace: true, state: { ...location.state, openWizard: false, wizardCosyflow: null } });
        }
    }, [location.state, navigate, location.pathname]);

    // Edit modal state
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [cosyflowToEdit, setCosyflowToEdit] = useState(null);

    // Creator profile modal state
    const [isCreatorModalOpen, setIsCreatorModalOpen] = useState(false);
    const [selectedCreator, setSelectedCreator] = useState(null);

    // Refresh key to trigger CosyflowPicker refetch
    const [cosyflowRefreshKey, setCosyflowRefreshKey] = useState(0);
    const [allCosyflows, setAllCosyflows] = useState([]);

    // Generated image modal (kept for potential future use)
    const [showImageModal, setShowImageModal] = useState(false);
    const [generatedImage] = useState(null);

    // Pricing state - category tariffs for cost display
    const [categoryTariffs, setCategoryTariffs] = useState(new Map());

    // Available models from get-models endpoint (for "Run in Playground" button visibility)
    const [availableModels, setAvailableModels] = useState([]);

    // Local available models from local ComfyUI/Cosy servers
    const [localAvailableModels, setLocalAvailableModels] = useState([]);

    // Setting for showing untrusted cosyflows
    const [useNonTrustedCosyflow, setUseNonTrustedCosyflow] = useState(false);

    // Load untrusted cosyflow setting on mount
    useEffect(() => {
        const loadSetting = async () => {
            try {
                const setting = await db.settings.get({ key: 'use_non_trusted_cosyflow' });
                setUseNonTrustedCosyflow(setting?.value || false);
            } catch (error) {
                console.error('[Cosyflows] Error loading use_non_trusted_cosyflow setting:', error);
            }
        };
        loadSetting();
    }, []);

    // Fetch category tariffs on mount
    useEffect(() => {
        const fetchTariffs = async () => {
            try {
                const apiBase = await getApiBaseUrl();
                console.log('[Cosyflows] Fetching category tariffs from:', `${apiBase}/api/cosyflows/categories`);

                const response = await axios.get(`${apiBase}/api/cosyflows/categories`, {
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${process.env.REACT_APP_API_TOKEN}`
                    }
                });

                console.log('[Cosyflows] Category tariffs response:', response.data);

                if (response.data?.categories && Array.isArray(response.data.categories)) {
                    const tariffMap = new Map();
                    response.data.categories.forEach((item) => {
                        if (item.cosyflow_category && item.cost !== undefined) {
                            const cost = typeof item.cost === 'string' ? parseFloat(item.cost) : item.cost;
                            if (!isNaN(cost)) {
                                tariffMap.set(item.cosyflow_category, cost);
                            }
                        }
                    });
                    setCategoryTariffs(tariffMap);
                    console.log('[Cosyflows] Loaded category tariffs:', tariffMap.size, 'categories', Array.from(tariffMap.entries()));
                }
            } catch (error) {
                console.error('[Cosyflows] Error fetching category tariffs:', error);
            }
        };

        fetchTariffs();
    }, []);

    // Fetch available models on mount (from Promptus cloud API)
    useEffect(() => {
        getOnlineAvailableModels().then(models => {
            console.log('[Cosyflows] Loaded available models:', models.length);
            setAvailableModels(models);
        });
    }, []);

    // Fetch local available models on mount (from local ComfyUI/Cosy servers)
    // This will fail silently if local servers are not running
    useEffect(() => {
        getLocalAvailableModels().then(models => {
            console.log('[Cosyflows] Loaded local available models:', models.length);
            setLocalAvailableModels(models);
        });
    }, [generateStatus.serverStatus]);

    // Get display cost for a category (with BASE_COST added)
    const getCostDisplay = useCallback((category) => {
        if (!category || categoryTariffs.size === 0) {
            // console.log('[Cosyflows] getCostDisplay: no category or empty tariffs', { category, size: categoryTariffs.size });
            return null;
        }

        // Direct match
        if (categoryTariffs.has(category)) {
            const cost = categoryTariffs.get(category) + BASE_COST;
            // console.log('[Cosyflows] getCostDisplay: direct match', { category, cost });
            return cost >= 1 ? Math.round(cost).toString() : cost.toString();
        }

        // Try matching by media type (last part of category path)
        const categoryParts = category.split('/');
        const mediaType = categoryParts[categoryParts.length - 1];
        const mainType = categoryParts[0];

        for (const [tariffCategory, cost] of categoryTariffs.entries()) {
            if (tariffCategory.endsWith('/' + mediaType) ||
                tariffCategory.startsWith(mainType + '/') ||
                tariffCategory === mainType ||
                tariffCategory.toLowerCase().includes(mediaType.toLowerCase())) {
                const displayCost = cost + BASE_COST;
                // console.log('[Cosyflows] getCostDisplay: fuzzy match', { category, tariffCategory, cost: displayCost });
                return displayCost >= 1 ? Math.round(displayCost).toString() : displayCost.toString();
            }
        }

        // console.log('[Cosyflows] getCostDisplay: no match found for', category);
        return null;
    }, [categoryTariffs]);

    // Detect output type from category
    const detectOutputType = useCallback((category) => {
        if (!category) return 'image';
        const lower = category.toLowerCase();
        if (lower.includes('video') || lower.includes('animation')) return 'video';
        if (lower.includes('audio') || lower.includes('music') || lower.includes('sound')) return 'audio';
        if (lower.includes('3d') || lower.includes('mesh') || lower.includes('model')) return '3d';
        return 'image';
    }, []);

    // Helper function to extract image URL from cosyflow data
    // Returns null if no image found - let CosyflowCard use picsum placeholder
    const extractImageUrl = useCallback((item) => {
        // Try to get image URL from direct properties first
        if (item.image_url) return item.image_url;
        if (item.imageUrl) return item.imageUrl;

        // Parse the cosyflow JSON to get image URL from definition
        if (item.cosyflow) {
            try {
                const cosyflowDef = typeof item.cosyflow === 'string'
                    ? JSON.parse(item.cosyflow)
                    : item.cosyflow;

                // Check cosyflow definition for image_url
                if (cosyflowDef?.image_url) return cosyflowDef.image_url;

                // Check metadata for example_urls or preview_images
                const exampleUrls = cosyflowDef?.metadata?.example_urls;
                const previewImages = cosyflowDef?.metadata?.preview_images;

                if (Array.isArray(exampleUrls) && exampleUrls.length > 0) {
                    return exampleUrls[0];
                }
                if (Array.isArray(previewImages) && previewImages.length > 0) {
                    return previewImages[0];
                }
            } catch (e) {
                console.warn('[Cosyflows] Failed to parse cosyflow JSON:', e);
            }
        }

        // Return null - CosyflowCard will use picsum.photos placeholder
        return null;
    }, []);

    // Transform raw API data to component format
    const transformCosyflowData = useCallback((items) => {
        return items.map(item => {
            const imageUrl = extractImageUrl(item);

            let hasComfyWorkflow = false;
            let creditsCost = item.credits_cost;
            let category = item.category || 'General';
            let speed = item.speed;
            let quality = item.quality;
            let popularity = item.popularity || item.usage_count || 0;
            let workers = item.workers || item.num_workers;
            let recommended = item.recommended || false;
            let tags = [];

            if (item.cosyflow) {
                try {
                    const cosyflowDef = typeof item.cosyflow === 'string'
                        ? JSON.parse(item.cosyflow)
                        : item.cosyflow;

                    if (cosyflowDef?.prompt && typeof cosyflowDef.prompt === 'object' && Object.keys(cosyflowDef.prompt).length > 0) {
                        hasComfyWorkflow = true;
                    }

                    if (cosyflowDef?.credits_cost !== undefined) {
                        creditsCost = cosyflowDef.credits_cost;
                    }

                    if (!item.category && cosyflowDef?.category) {
                        category = cosyflowDef.category;
                    }

                    const metadata = cosyflowDef?.metadata || {};
                    if (metadata.speed !== undefined) speed = metadata.speed;
                    if (metadata.quality !== undefined) quality = metadata.quality;
                    if (metadata.popularity !== undefined) popularity = metadata.popularity;
                    if (metadata.workers !== undefined) workers = metadata.workers;
                    if (metadata.recommended !== undefined) recommended = metadata.recommended;
                    if (cosyflowDef.tags && Array.isArray(cosyflowDef.tags) && cosyflowDef.tags.length > 0) {
                        tags = cosyflowDef.tags;
                    } else if (metadata.tags && Array.isArray(metadata.tags)) {
                        tags = metadata.tags;
                    }
                } catch (e) {
                    console.error('[Cosyflows transformCosyflows] Parse error for:', item.title, e);
                }
            }

            return {
                id: item.id || item.uid,
                uid: item.uid,
                title: item.title || item.name,
                description: item.description || '',
                category: category,
                imageUrl: imageUrl,
                outputType: detectOutputType(category),
                creator: item.alias || item.creator,
                accountNo: item.account_no || item.accountNo,
                trusted: item.trusted === 1 || item.trusted === "1" || item.trusted === true,
                isPublic: item.public === 1 || item.public === "1" || item.public === true,
                distributed: item.distributed === 1 || item.distributed === "1" || item.distributed === true,
                credits_cost: creditsCost,
                model: item.title,
                prompt: item.base_prompt || '',
                has_image: !!imageUrl,
                cosyflow: item.cosyflow,
                hasComfyWorkflow: hasComfyWorkflow,
                speed: speed,
                quality: quality,
                popularity: popularity,
                workers: workers,
                recommended: recommended,
                created_date: item.created || item.created_date || item.date_created,
                updated_date: item.updated || item.updated_date || item.date_updated,
                tags: tags,
            };
        }).filter(item => {
            // Filter out cosyflows with unsupported variable types
            if (!item.cosyflow) return true; // No cosyflow data = can't check, allow through
            try {
                const cosyflowDef = typeof item.cosyflow === 'string'
                    ? JSON.parse(item.cosyflow) : item.cosyflow;
                if (cosyflowDef?.variables && !hasSupportedTypes(cosyflowDef.variables)) {
                    console.log(`[Cosyflows] Hiding "${item.title}" — unsupported types:`, getUnsupportedTypes(cosyflowDef.variables));
                    return false;
                }
            } catch (e) { /* parse error, allow through */ }
            return true;
        });
    }, [detectOutputType, extractImageUrl]);

    const fetchFromAPIAndCache = useCallback(async () => {
        try {
            const apiBase = await getApiBaseUrl();

            console.log('[Cosyflows] Fetching all cosyflows from API...');
            const response = await axios.get(`${apiBase}/api/cosyflows/query`, {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${process.env.REACT_APP_API_TOKEN}`
                }
            });

            const cosyflows = response.data?.cosyflows || [];
            console.log('[Cosyflows] Received', cosyflows.length, 'cosyflows from API');

            if (cosyflows.length > 0) {
                const transformed = transformCosyflowData(cosyflows);

                try {
                    await db.settings.put({
                        key: CACHE_KEY,
                        value: {
                            transformedCosyflows: transformed,
                            timestamp: Date.now()
                        }
                    });
                    console.log('[Cosyflows] Cached', transformed.length, 'transformed cosyflows');
                } catch (cacheError) {
                    console.warn('[Cosyflows] Failed to cache:', cacheError);
                }

                return transformed;
            }
            return [];
        } catch (error) {
            console.error('[Cosyflows] Error fetching from API:', error);
            throw error;
        }
    }, [transformCosyflowData]);

    // Check if cache is stale by comparing count with API
    const checkAndSyncCache = useCallback(async (delayMs = 3000) => {
        try {
            if (delayMs > 0) {
                await new Promise(resolve => setTimeout(resolve, delayMs));
            }

            const apiBase = await getApiBaseUrl();
            const response = await axios.get(`${apiBase}/api/cosyflows/query`, {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${process.env.REACT_APP_API_TOKEN}`
                }
            });

            const apiCosyflows = response.data?.cosyflows || [];
            const apiCount = apiCosyflows.length;

            // Compare with cached count
            const cachedData = await db.settings.get(CACHE_KEY);
            const cachedCount = cachedData?.value?.transformedCosyflows?.length || 0;

            if (apiCount !== cachedCount) {
                console.log(`[Cosyflows] Cache stale: cached ${cachedCount} vs API ${apiCount} — refreshing cache`);
                const transformed = transformCosyflowData(apiCosyflows);

                await db.settings.put({
                    key: CACHE_KEY,
                    value: {
                        transformedCosyflows: transformed,
                        timestamp: Date.now()
                    }
                });

                setAllCosyflows(transformed);
                setCosyflowRefreshKey(prev => prev + 1);
                console.log('[Cosyflows] Cache synced with', transformed.length, 'cosyflows');
            } else {
                console.log(`[Cosyflows] Cache is up to date (${cachedCount} items)`);
            }
        } catch (error) {
            console.warn('[Cosyflows] Cache sync check failed:', error);
        }
    }, [transformCosyflowData]);

    // Check cache freshness on page entry (with 3s API buffer)
    useEffect(() => {
        checkAndSyncCache(3000);
    }, [checkAndSyncCache]);

    // Fetch cosyflows with IndexedDB caching for instant loading
    const fetchCosyflowsData = useCallback(async () => {
        try {
            // Check cache first for instant loading
            const cachedData = await db.settings.get(CACHE_KEY);
            if (cachedData?.value) {
                const { transformedCosyflows, timestamp } = cachedData.value;
                const cacheAge = Date.now() - timestamp;

                // Return cached transformed data if valid
                if (transformedCosyflows && transformedCosyflows.length > 0 && cacheAge < CACHE_DURATION) {
                    console.log('[Cosyflows] Using cached data:', transformedCosyflows.length, 'items, age:', Math.round(cacheAge / 1000 / 60), 'minutes');

                    // Store for creator profile modal
                    setAllCosyflows(transformedCosyflows);

                    // Refresh in background if cache is older than 1 hour
                    if (cacheAge > 60 * 60 * 1000) {
                        console.log('[Cosyflows] Cache is older than 1 hour, refreshing in background...');
                        fetchFromAPIAndCache().then(data => {
                            if (data) setAllCosyflows(data);
                        }).catch(err => {
                            console.warn('[Cosyflows] Background refresh failed:', err);
                        });
                    }

                    return transformedCosyflows;
                }
            }

            // Cache miss or expired - fetch from API
            console.log('[Cosyflows] Cache miss, fetching from API...');
            const data = await fetchFromAPIAndCache();
            setAllCosyflows(data);
            return data;
        } catch (error) {
            console.error('[Cosyflows] Error in fetchCosyflowsData:', error);
            // On error, try to return any cached data even if expired
            try {
                const cachedData = await db.settings.get(CACHE_KEY);
                if (cachedData?.value?.transformedCosyflows) {
                    console.log('[Cosyflows] Using expired cache due to error');
                    setAllCosyflows(cachedData.value.transformedCosyflows);
                    return cachedData.value.transformedCosyflows;
                }
            } catch (e) {
                // Ignore cache errors
            }
            return [];
        }
    }, [fetchFromAPIAndCache]);

    // Fetch user's cosyflows only
    const fetchUserCosyflowsData = useCallback(async () => {
        const accountNo = accountContext.accountDetails?.accountNo;
        if (!accountNo) {
            console.log('[Cosyflows] No accountNo available, returning empty array');
            return [];
        }

        try {
            const apiBase = await getApiBaseUrl();
            console.log('[Cosyflows] Fetching user cosyflows from API:', `${apiBase}/api/cosyflows/query?account_no=${accountNo}&trusted=`);

            const response = await axios.get(`${apiBase}/api/cosyflows/query?account_no=${accountNo}&trusted=`, {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${getAuthorizationToken()}`
                }
            });

            if (response.data && response.data.cosyflows) {
                console.log('[Cosyflows] Received', response.data.cosyflows.length, 'user cosyflows from API');

                return response.data.cosyflows.map(item => {
                    const imageUrl = extractImageUrl(item);

                    let hasComfyWorkflow = false;
                    let creditsCost = item.credits_cost;
                    let category = item.category || 'General';
                    let speed = item.speed;
                    let quality = item.quality;
                    let popularity = item.popularity || item.usage_count || 0;
                    let workers = item.workers || item.num_workers;
                    let recommended = item.recommended || false;
                    let tags = [];

                    if (item.cosyflow) {
                        try {
                            const cosyflowDef = typeof item.cosyflow === 'string'
                                ? JSON.parse(item.cosyflow)
                                : item.cosyflow;

                            if (cosyflowDef?.prompt && typeof cosyflowDef.prompt === 'object' && Object.keys(cosyflowDef.prompt).length > 0) {
                                hasComfyWorkflow = true;
                            }

                            if (cosyflowDef?.credits_cost !== undefined) {
                                creditsCost = cosyflowDef.credits_cost;
                            }

                            if (!item.category && cosyflowDef?.category) {
                                category = cosyflowDef.category;
                            }

                            const metadata = cosyflowDef?.metadata || {};
                            if (metadata.speed !== undefined) speed = metadata.speed;
                            if (metadata.quality !== undefined) quality = metadata.quality;
                            if (metadata.popularity !== undefined) popularity = metadata.popularity;
                            if (metadata.workers !== undefined) workers = metadata.workers;
                            if (metadata.recommended !== undefined) recommended = metadata.recommended;
                            if (cosyflowDef.tags && Array.isArray(cosyflowDef.tags) && cosyflowDef.tags.length > 0) {
                                tags = cosyflowDef.tags;
                            } else if (metadata.tags && Array.isArray(metadata.tags)) {
                                tags = metadata.tags;
                            }
                        } catch (e) {
                            console.error('[Cosyflows loadMyCosyflows] Parse error for:', item.title, e);
                        }
                    }

                    return {
                        id: item.id || item.uid,
                        uid: item.uid,
                        title: item.title || item.name,
                        description: item.description || '',
                        category: category,
                        imageUrl: imageUrl,
                        outputType: detectOutputType(category),
                        creator: item.alias || item.creator,
                        accountNo: item.account_no || item.accountNo,
                        trusted: item.trusted === 1 || item.trusted === "1" || item.trusted === true,
                        isPublic: item.public === 1 || item.public === "1" || item.public === true,
                        distributed: item.distributed === 1 || item.distributed === "1" || item.distributed === true,
                        credits_cost: creditsCost,
                        model: item.title,
                        prompt: item.base_prompt || '',
                        has_image: !!imageUrl,
                        cosyflow: item.cosyflow,
                        hasComfyWorkflow: hasComfyWorkflow,
                        speed: speed,
                        quality: quality,
                        popularity: popularity,
                        workers: workers,
                        recommended: recommended,
                        created_date: item.created || item.created_date || item.date_created,
                        updated_date: item.updated || item.updated_date || item.date_updated,
                        tags: tags,
                    };
                });
            }

            return [];
        } catch (error) {
            console.error('[Cosyflows] Error fetching user cosyflows:', error);
            return [];
        }
    }, [accountContext.accountDetails?.accountNo, detectOutputType, extractImageUrl]);

    // Fetch available models from the API (for Available tab filtering)
    const fetchAvailableModelsData = useCallback(async () => {
        try {
            const apiBase = await getApiBaseUrl();
            const response = await axios.get(`${apiBase}/api/generate/get-models`, {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${getAuthorizationToken()}`
                }
            });
            console.log('[Cosyflows] Fetched available models:', response.data.models?.length || 0);
            return response.data.models || [];
        } catch (error) {
            console.error('[Cosyflows] Error fetching available models:', error);
            return [];
        }
    }, []);

    // ============================================
    // Wizard API Functions
    // ============================================

    // Fetch models for wizard dropdown
    const fetchModels = useCallback(async () => {
        try {
            const cosyflows = await fetchCosyflowsData();
            return cosyflows.map(c => ({ id: c.id, title: c.title, name: c.title }));
        } catch (error) {
            console.error('Error fetching models:', error);
            return [];
        }
    }, [fetchCosyflowsData]);

    // Fetch model defaults/schema
    const fetchModelDefaults = useCallback(async (modelName) => {
        if (!modelName) return null;

        try {
            const apiBase = await getApiBaseUrl();
            const response = await axios.get(`${apiBase}/api/cosyflows/query?title=${encodeURIComponent(modelName)}`, {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${process.env.REACT_APP_API_TOKEN}`
                }
            });

            if (response.data.cosyflows && response.data.cosyflows.length > 0) {
                const cosyflow = response.data.cosyflows[0];
                if (cosyflow.cosyflow) {
                    const cosyflowData = typeof cosyflow.cosyflow === 'string'
                        ? JSON.parse(cosyflow.cosyflow)
                        : cosyflow.cosyflow;
                    const defaults = {};
                    const foundImageFields = [];

                    if (cosyflowData.variables) {
                        for (const [key, obj] of Object.entries(cosyflowData.variables)) {
                            if (obj && typeof obj === "object") {
                                const imageTypes = ['IMAGE', 'b64data', 'file', 'b64', 'image'];
                                if (obj.type && imageTypes.includes(obj.type)) {
                                    foundImageFields.push(key);
                                }
                                if ("default" in obj) {
                                    defaults[key] = obj.default;
                                }
                            }
                        }
                    }

                    return { defaults, imageFieldNames: foundImageFields, schema: cosyflowData.variables };
                }
            }
            return null;
        } catch (error) {
            console.error('Error fetching model defaults:', error);
            return null;
        }
    }, []);

    // Fetch full cosyflow definition
    const fetchCosyflow = useCallback(async (modelName) => {
        if (!modelName) return null;

        try {
            const apiBase = await getApiBaseUrl();
            const response = await axios.get(`${apiBase}/api/cosyflows/query?title=${encodeURIComponent(modelName)}&trusted=`, {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${process.env.REACT_APP_API_TOKEN}`
                }
            });

            if (response.data.cosyflows && response.data.cosyflows.length > 0) {
                const cosyflow = response.data.cosyflows[0];

                let variables = null;
                if (cosyflow.cosyflow) {
                    try {
                        const cosyflowData = typeof cosyflow.cosyflow === 'string'
                            ? JSON.parse(cosyflow.cosyflow)
                            : cosyflow.cosyflow;
                        variables = cosyflowData?.variables || null;
                        console.log('[fetchCosyflow] Parsed variables:', variables);
                    } catch (e) {
                        console.error('[fetchCosyflow] Error parsing cosyflow JSON:', e);
                    }
                }

                return {
                    title: cosyflow.title,
                    description: cosyflow.description,
                    category: cosyflow.category,
                    image_url: cosyflow.image_url,
                    credits_cost: cosyflow.credits_cost,
                    variables: variables,
                };
            }
            return null;
        } catch (error) {
            console.error('Error fetching cosyflow:', error);
            return null;
        }
    }, []);

    // Generate content from prompt
    
    const generateContent = useCallback(async ({ prompt, basePrompt, model, modelDefaults, imageInputs, fieldValues, onProgress }) => {
        const accountNo = accountContext.accountDetails?.accountNo;
        if (!accountNo) {
            throw new Error('Account not found. Please login first.');
        }

        const setNestedProperty = (obj, path, value) => {
            const keys = path.split('.');
            const lastKey = keys.pop();
            const target = keys.reduce((acc, key) => {
                if (!acc[key]) acc[key] = {};
                return acc[key];
            }, obj);
            target[lastKey] = value;
        };

        const params = {};

        // Apply defaults
        if (modelDefaults?.defaults) {
            for (const [key, value] of Object.entries(modelDefaults.defaults)) {
                if (key.includes('.')) {
                    setNestedProperty(params, key, value);
                } else {
                    params[key] = value;
                }
            }
        }

        // Find and set prompt field
        const fullPrompt = basePrompt ? `${basePrompt}, ${prompt}` : prompt;
        if (modelDefaults?.schema) {
            for (const [key, fieldDef] of Object.entries(modelDefaults.schema)) {
                if (fieldDef && fieldDef.type === 'prompt') {
                    if (key.includes('.')) {
                        setNestedProperty(params, key, fullPrompt);
                    } else {
                        params[key] = fullPrompt;
                    }
                    break;
                }
            }
        }

        // Set image inputs
        if (imageInputs && modelDefaults?.imageFieldNames) {
            for (const fieldName of modelDefaults.imageFieldNames) {
                const preview = imageInputs[fieldName];
                if (preview) {
                    let base64Image = preview;
                    if (preview.includes('base64,')) {
                        base64Image = preview.split('base64,')[1];
                    }
                    if (fieldName.includes('.')) {
                        setNestedProperty(params, fieldName, base64Image);
                    } else {
                        params[fieldName] = base64Image;
                    }
                }
            }
        }

        // Apply fieldValues from DynamicPromptForm
        if (fieldValues) {
            for (const [key, value] of Object.entries(fieldValues)) {
                if (value !== undefined && value !== null && value !== '') {
                    let finalValue = value;
                    if (key === 'prompt' && basePrompt) {
                        finalValue = `${basePrompt}, ${value}`;
                    }
                    if (key.includes('.')) {
                        setNestedProperty(params, key, finalValue);
                    } else {
                        params[key] = finalValue;
                    }
                }
            }
        }

        if (modelDefaults?.schema) {
            const missingFields = [];
            for (const [key, fieldDef] of Object.entries(modelDefaults.schema)) {
                if (key === 'cosyflow' || key === 'cosyflow_template') continue;
                const isRequired = fieldDef.importance === 5 || fieldDef.required === true;
                if (!isRequired) continue;
                if (fieldDef.type === 'boolean' || fieldDef.type === 'BOOLEAN') continue;
                if (fieldDef.type === 'seed' || fieldDef.type === 'SEED') continue;
                if (fieldDef.type === 'INT' || fieldDef.type === 'FLOAT' || fieldDef.type === 'number' || fieldDef.type === 'enum' || fieldDef.type === 'COMBO' || fieldDef.type === 'COMFY_DYNAMICCOMBO_V3') continue;
                const resolvedKey = key.includes('.') ? key.split('.').reduce((obj, k) => obj?.[k], params) : params[key];
                if (!resolvedKey && resolvedKey !== 0 && resolvedKey !== false) {
                    if (fieldDef.default !== undefined && fieldDef.default !== null && String(fieldDef.default).trim() !== '') continue;
                    const label = (fieldDef.title || key).split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
                    missingFields.push(label);
                }
            }
            if (missingFields.length > 0) {
                throw new Error(`Required fields missing: ${missingFields.join(', ')}`);
            }
        }

        if (!model || model.trim() === '') {
            throw new Error('No model/cosyflow specified. Cannot generate content.');
        }

        const templateSrc = electronBridge.isElectron ? 'PWA' : 'PWeb' + '_Template'

        const promptData = {
            prompt: {
                ...params,
                count: 1,
                cosyflow: model
            },
            accountNo,
            source: templateSrc,
            version: process.env.REACT_APP_VERSION
        };

        try {
            const response = await fetch('https://api.ipify.org');
            promptData.ip_address = await response.text();
        } catch (e) {
            promptData.ip_address = '';
        }

        const data = await generatePrompt(promptData, apiState, generateStatusDispatch);

        if (data.error) {
            throw new Error(data.error);
        }

        const jobId = data.uid;
        if (!jobId) throw new Error('No job ID returned');

        const isCosyActive = jobId.includes("-");

        const getMediaTypeFromUrl = (url) => {
            if (!url) return 'image';
            const lower = url.toLowerCase();
            if (lower.includes('.mp4') || lower.includes('.webm') || lower.includes('.mov')) return 'video';
            if (lower.includes('.mp3') || lower.includes('.wav') || lower.includes('.ogg') || lower.includes('.flac')) return 'audio';
            if (lower.includes('.glb') || lower.includes('.gltf') || lower.includes('.obj')) return '3d';
            return 'image';
        };

        const TIMEOUT_DURATION = 90000; // 90 seconds
        let lastProgressTime = Date.now();
        let lastProgress = -1;

        while (true) {
            await new Promise(resolve => setTimeout(resolve, 2000));

            // Check for timeout (no progress for 90 seconds)
            const timeSinceLastProgress = Date.now() - lastProgressTime;
            if (timeSinceLastProgress > TIMEOUT_DURATION) {
                console.error(`[Cosyflows] Generation timed out - no progress for ${Math.round(timeSinceLastProgress / 1000)} seconds`);
                throw new Error('Generation timed out - no progress for 90 seconds');
            }

            try {
                const progressData = await getProgress(jobId, isCosyActive, apiState, generateStatusDispatch);
                console.log('[Cosyflows] Progress data:', progressData);

                const progress = progressData.percent || progressData.progress || 0;
                if (progress > 0) {
                    onProgress?.(progress);

                    // Reset timeout if progress changed
                    if (progress !== lastProgress) {
                        console.log(`[Cosyflows] Progress updated: ${lastProgress}% -> ${progress}%, resetting timeout`);
                        lastProgressTime = Date.now();
                        lastProgress = progress;
                    }
                }

                if (progressData.error && progressData.error !== "None") {
                    throw new Error(progressData.error);
                }

                if (progressData.output && Array.isArray(progressData.output) && progressData.output.length > 0) {
                    const outputItem = progressData.output[0];
                    const outputUid = outputItem.uid;

                    if (outputUid) {
                        console.log('[Cosyflows] Fetching output for:', outputUid);

                        let outputData = null;
                        for (let retry = 0; retry < 5; retry++) {
                            try {
                                outputData = await getGenerateOutput(jobId, outputUid);
                                if (outputData && (outputData.url || outputData.image)) {
                                    break;
                                }
                            } catch (err) {
                                console.log(`[Cosyflows] Retry ${retry + 1} for getGenerateOutput`);
                            }
                            await new Promise(resolve => setTimeout(resolve, 1000));
                        }

                        if (outputData) {
                            onProgress?.(100);
                            console.log('[Cosyflows] outputData:', outputData);

                            const isUrl = (str) => {
                                if (!str) return false;
                                const trimmed = str.trim().toLowerCase();
                                return trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('data:');
                            };

                            const mediaType = getMediaTypeFromUrl(outputData.url || outputData.image);
                            let imageData;

                            if (outputData.url && outputData.url.trim() !== '') {
                                imageData = outputData.url;
                            } else if (outputData.image) {
                                if (isUrl(outputData.image)) {
                                    imageData = outputData.image;
                                } else {
                                    const prefix = mediaType === 'video' ? 'data:video/mp4;base64,' :
                                                   mediaType === 'audio' ? `data:${getAudioMimeFromBase64(outputData.image)};base64,` :
                                                   'data:image/webp;base64,';
                                    imageData = `${prefix}${outputData.image}`;
                                }
                            }

                            console.log('[Cosyflows] Final imageData:', imageData?.substring(0, 100));

                            const saveResult = await saveGeneratedMedia({
                                imageData: imageData,
                                base64Data: outputData.image,
                                jobUid: jobId,
                                outputUid: outputUid,
                                seed: outputData.seed || null,
                                model: model || '',
                                prompt: fullPrompt || '',
                                tag: '',
                                isOnline: true,
                                serverUrl: outputData.url || null,
                                dataType: mediaType,
                            });

                            console.log('[Cosyflows] Media saved to DB:', saveResult);

                            return {
                                image: imageData,
                                serverUrl: outputData.url || null,
                                type: mediaType,
                                dbId: saveResult.id,
                                hash: saveResult.hash
                            };
                        }
                    }
                }
            } catch (err) {
                console.error('[Cosyflows] Error polling progress:', err);
                if (err.message && !err.message.includes('timeout')) {
                    throw err;
                }
            }
        }
    }, [accountContext.accountDetails, apiState, generateStatusDispatch]);

    // Create template
    const createTemplate = useCallback(async ({ formData, cosyflowDefinition, accountNo }) => {
        try {
            const apiBase = await getApiBaseUrl();

            const exampleUrls = formData.galleryItems?.map(item => item.serverUrl || item.imageUrl).filter(Boolean) || [];

            // Filter variables to only importance 5 + required fields, plus always include cosyflow_template
            const filteredVariables = {};
            if (cosyflowDefinition?.variables) {
                for (const [key, field] of Object.entries(cosyflowDefinition.variables)) {
                    if (field.importance === 5 && field.required === true) {
                        filteredVariables[key] = field;
                    }
                }
            }
            // Always include cosyflow_template pointing to the base cosyflow
            filteredVariables.cosyflow_template = {
                type: "string",
                default: cosyflowDefinition?.title || formData.model || "",
                description: "Base cosyflow this template is built on",
                importance: 1,
                required: true
            };

            const cosyflowInner = {
                title: formData.title,
                description: formData.description,
                category: formData.category,
                variables: filteredVariables,
                metadata: {
                    example_urls: exampleUrls
                },
                workflow: cosyflowDefinition?.workflow || {}
            };

            const templateData = {
                account_no: accountNo,
                trusted: false,
                public: true,
                distributed: false,
                title: formData.title,
                category: formData.category,
                description: formData.description,
                cosyflow: JSON.stringify(cosyflowInner)
            };

            const response = await axios.post(`${apiBase}/api/cosyflows/create`, templateData, {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${getAuthorizationToken()}`
                }
            });

            if (response.data.error) {
                return { error: response.data.error };
            }

            const templateId = response.data.uid
                || response.data.id
                || response.data.template_id
                || response.data.cosyflow_id
                || response.data?.cosyflow?.uid;

            return { id: templateId };
        } catch (error) {
            console.error('Error creating template:', error);
            return { error: error.message || 'Failed to create template' };
        }
    }, []);

    // Update cosyflow API function (like original templates app)
    const updateCosyflow = useCallback(async ({
        accountNo,
        uid,
        title,
        description,
        category,
        basePrompt,
        isPublic,
        distributed,
        cosyflowJson,
        exampleImages,
    }) => {
        try {
            const apiBase = await getApiBaseUrl();

            const requestData = {
                account_no: accountNo,
                uid: uid,
                public: isPublic ? 1 : 0,
                distributed: distributed ? 1 : 0,
                title: title,
                category: category,
                description: description,
            };

            // Include base_prompt if provided
            if (basePrompt) {
                requestData.base_prompt = basePrompt;
            }

            // Include cosyflow JSON if provided
            if (cosyflowJson) {
                requestData.cosyflow = typeof cosyflowJson === 'string'
                    ? cosyflowJson
                    : JSON.stringify(cosyflowJson);
            }

            // Include example_images if provided
            if (exampleImages && Array.isArray(exampleImages)) {
                requestData.example_images = exampleImages;
            }

            console.log('[Cosyflows] Updating cosyflow:', { uid, title, exampleImagesCount: exampleImages?.length });

            const response = await axios.post(`${apiBase}/api/cosyflows/update`, requestData, {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${process.env.REACT_APP_API_TOKEN}`
                }
            });

            if (response.data?.error) {
                return { error: response.data.error };
            }

            return { success: true };
        } catch (error) {
            console.error('[Cosyflows] Error updating cosyflow:', error);
            return { error: error.message || 'Failed to update cosyflow' };
        }
    }, []);

    // Cosyflow handlers from shared hook
    const cosyflowHandlers = useCosyflowHandlers({
        navigate,
        accountNo: accountContext.accountDetails?.accountNo,
        accountAlias: accountContext.accountDetails?.alias,
        startInstall,
        updateCosyflow,
        onOpenEditModal: (cosyflow) => {
            setCosyflowToEdit(cosyflow);
            setIsEditModalOpen(true);
        },
        onOpenWizard: (cosyflow) => {
            setSelectedCosyflow(cosyflow);
            setIsWizardOpen(true);
        },
        onRefresh: () => setCosyflowRefreshKey(prev => prev + 1),
    });

    // SweetAlert wrapper
    const showAlert = useCallback(async (config) => {
        const result = await Swal.fire({
            title: config.title,
            text: config.text,
            icon: config.icon,
            confirmButtonText: config.confirmButtonText || 'OK',
            showCancelButton: config.showCancelButton || false,
            cancelButtonText: config.cancelButtonText || 'Cancel',
            confirmButtonColor: '#FFB02E',
            didOpen: () => {
                const container = document.querySelector('.swal2-container');
                if (container) container.style.zIndex = '99999';
            },
        });
        return { isConfirmed: result.isConfirmed };
    }, []);

    const handleBack = useCallback(() => {
        navigate('/');
    }, [navigate]);

    // Handle cosyflow selection from CosyflowPicker - navigate to detail page
    const handleCosyflowSelect = useCallback((cosyflow) => {
        console.log('[Cosyflows] Cosyflow selected:', cosyflow.title);
        console.log('[Cosyflows] Full cosyflow card data:', JSON.parse(JSON.stringify(cosyflow)));
        const templateId = cosyflow.uid || cosyflow.id;
        navigate(`/cosyflows/${templateId}`, {
            state: {
                cosyflowData: cosyflow,
                availableModels,
                localAvailableModels
            }
        });
    }, [navigate, availableModels, localAvailableModels]);

    // Handle view details button click - navigate to detail page
    const handleViewDetails = useCallback((cosyflow) => {
        console.log('[Cosyflows] View details:', cosyflow.title);
        console.log('[Cosyflows] Full cosyflow card data:', JSON.parse(JSON.stringify(cosyflow)));
        const templateId = cosyflow.uid || cosyflow.id;
        navigate(`/cosyflows/${templateId}`, {
            state: {
                cosyflowData: cosyflow,
                availableModels,
                localAvailableModels
            }
        });
    }, [navigate, availableModels, localAvailableModels]);

    const handleWizardClose = useCallback(() => {
        setIsWizardOpen(false);
        setSelectedCosyflow(null);
        // Check for new cosyflows in case one was created
        checkAndSyncCache(3000);
    }, [checkAndSyncCache]);

    const handleWizardSuccess = useCallback((templateId) => {
        console.log('[Cosyflows] Template created:', templateId, '— checking cache in 3s...');
        checkAndSyncCache(3000);
    }, [checkAndSyncCache]);

    const handleNavigateToTemplate = useCallback((templateId) => {
        navigate(`/cosyflows/${templateId}`);
    }, [navigate]);

    const handleEditModalClose = useCallback(() => {
        setIsEditModalOpen(false);
        setCosyflowToEdit(null);
    }, []);

    const handleEditModalSave = useCallback((updatedData) => {
        console.log('[Cosyflows] Cosyflow updated:', updatedData);
        Swal.fire({
            title: 'Success!',
            text: 'Your cosyflow has been updated.',
            icon: 'success',
            confirmButtonColor: '#FFB02E'
        });
        // Note: The list will refresh on next page load
        // For immediate update, we could trigger a refetch here
    }, []);

    // Handle creator click - show creator profile modal
    const handleCreatorClick = useCallback(({ creator, accountNo }) => {
        console.log('[Cosyflows] Creator clicked:', creator, accountNo);
        setSelectedCreator({ creator, accountNo });
        setIsCreatorModalOpen(true);
    }, []);

    const handleCreatorModalClose = useCallback(() => {
        setIsCreatorModalOpen(false);
        setSelectedCreator(null);
    }, []);

    return (
        <div className="relative bg-dark-blue min-h-screen">
            {/* CosyflowPicker Component */}
            <CosyflowPicker
                fetchCosyflows={fetchCosyflowsData}
                fetchUserCosyflows={fetchUserCosyflowsData}
                refreshKey={cosyflowRefreshKey}
                fetchAvailableModels={fetchAvailableModelsData}
                onSelect={handleCosyflowSelect}
                onBack={null}
                onRunInPlayground={cosyflowHandlers.handleRunInPlayground}
                onRunOfflineInPlayground={cosyflowHandlers.handleRunOfflineInPlayground}
                onRunInCosyUI={cosyflowHandlers.handleRunInCosyUI}
                onRunInComfyUI={cosyflowHandlers.handleRunInComfyUI}
                onOpenEditor={cosyflowHandlers.handleOpenEditor}
                onCreateTemplate={cosyflowHandlers.handleCreateTemplate}
                onInstallLocally={cosyflowHandlers.handleInstallLocally}
                isInstalling={isInstalling}
                onModifyCosyflow={cosyflowHandlers.handleModifyCosyflow}
                onDeleteCosyflow={cosyflowHandlers.handleDeleteCosyflow}
                onViewDetails={handleViewDetails}
                onCreatorClick={handleCreatorClick}
                onMakeAvailableLocally={cosyflowHandlers.handleMakeAvailableLocally}
                onMakeAvailableOnline={cosyflowHandlers.handleMakeAvailableOnline}
                onCreateFromDistributed={cosyflowHandlers.handleCreateFromDistributed}
                onTogglePublic={cosyflowHandlers.handleTogglePublic}
                onToggleDistributed={cosyflowHandlers.handleToggleDistributed}
                currentUserAccountNo={accountContext.accountDetails?.accountNo}
                currentUserAlias={accountContext.accountDetails?.alias}
                isStaffAccount={isStaff(accountContext.accountDetails)}
                isStaffOrCreatorAccount={isStaffOrCreator(accountContext.accountDetails)}
                showOwnershipFilter={true}
                initialShowOnlyMine={initialShowOnlyMine}
                getCostDisplay={getCostDisplay}
                availableModels={availableModels}
                localAvailableModels={localAvailableModels}
                allowUntrusted={useNonTrustedCosyflow}
                title="Cosyflows"
                subtitle="Browse cosyflows and choose an action"
                backText="Back to Playground"
                searchPlaceholder="Search cosyflows..."
                showHeader={true}
                showActionButtons={true}
                classNames={COSYFLOW_PICKER_CLASS_NAMES}
            />

            {/* CreateTemplateWizard */}
            <CreateTemplateWizard
                isOpen={isWizardOpen}
                onClose={handleWizardClose}
                preSelectedCosyflow={selectedCosyflow}
                onClearPreSelected={() => setSelectedCosyflow(null)}
                isLoggedIn={!!accountContext.accountDetails?.accountNo}
                accountNo={accountContext.accountDetails?.accountNo}
                username={accountContext.accountDetails?.alias}
                fetchModels={fetchModels}
                fetchModelDefaults={fetchModelDefaults}
                fetchCosyflow={fetchCosyflow}
                generateContent={generateContent}
                createTemplate={createTemplate}
                onSuccess={handleWizardSuccess}
                onNavigateToTemplate={handleNavigateToTemplate}
                showAlert={showAlert}
                title="Create Template"
            />

            {/* Generated Image Modal */}
            <Modal
                open={showImageModal}
                onClose={() => setShowImageModal(false)}
                center
                container={document.querySelector('.main-content') || undefined}
                closeIcon={<CloseIcon className="fill-white" />}
                classNames={{ modal: 'customModal', overlay: 'customOverlay' }}
                styles={{
                    modal: {
                        background: '#190A4E',
                        border: '2px solid rgba(0, 217, 255, 0.5)',
                        borderRadius: '12px',
                        padding: '1.5rem',
                        maxWidth: '56rem',
                        width: '90%',
                        maxHeight: '90vh',
                        overflowY: 'auto'
                    },
                    overlay: { background: 'rgba(0, 0, 0, 0.8)' },
                    closeButton: { width: '24px', height: '24px', top: '12px', right: '12px' }
                }}
            >
                {generatedImage && (
                    <div className="text-center">
                        <h2 className="text-2xl font-bold text-white mb-4">Your Generated Image</h2>
                        <img
                            src={generatedImage.image?.startsWith('data:') ? generatedImage.image : `data:image/webp;base64,${generatedImage.image}`}
                            alt="Generated"
                            className="max-w-full h-auto rounded-lg mb-4 mx-auto"
                        />
                        <div className="flex gap-3 justify-center">
                            <button
                                onClick={() => setShowImageModal(false)}
                                className="bg-dark-blue/50 hover:bg-dark-blue/70 text-white font-bold py-3 px-6 rounded-lg transition-all border border-white/20"
                            >
                                Close
                            </button>
                            <button
                                onClick={() => { setShowImageModal(false); navigate('/'); }}
                                className="bg-yellow hover:bg-yellow/90 text-dark-blue font-bold py-3 px-6 rounded-lg transition-all"
                            >
                                Go to Playground
                            </button>
                        </div>
                    </div>
                )}
            </Modal>

            {/* EditCosyflowModal */}
            <EditCosyflowModal
                cosyflow={cosyflowToEdit}
                isOpen={isEditModalOpen}
                onClose={handleEditModalClose}
                onSave={handleEditModalSave}
                updateCosyflow={updateCosyflow}
                accountNo={accountContext.accountDetails?.accountNo}
                generateContent={generateContent}
                fetchModelDefaults={fetchModelDefaults}
            />

            {/* CreatorProfileModal */}
            <CreatorProfileModal
                isOpen={isCreatorModalOpen}
                onClose={handleCreatorModalClose}
                creator={selectedCreator?.creator}
                accountNo={selectedCreator?.accountNo}
                allCosyflows={allCosyflows}
                onCosyflowSelect={handleCosyflowSelect}
                getCostDisplay={getCostDisplay}
            />
        </div>
    );
}
